// App-root companion to PersonalNarrationProvider.
//
// Two surfaces, both global:
//
//   1. **Toast fires on terminal state change**: ready, failed,
//      or quota exhausted. Listens to the context's ``activeJob``
//      and fires react-toastify on transitions out of 'pending' /
//      'submitting'. The toast is the writer's confirmation that
//      their voice landed even if they navigated away from the
//      reader -- which is the whole point of the bg-job pattern.
//
//   2. **Floating "Generating..." badge** while a job is pending.
//      Sticks to bottom-right (above any other toasts) with a
//      tap-to-jump-back-to-the-reader affordance. Shows the
//      elapsed timer so the writer can decide whether to wait or
//      get coffee.
//
// Both fire only for the personal-narrator (F5-TTS) tier --
// Polly Pro's polling is in-tab + fast, no bg pattern needed
// there.

import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrophoneLines,
  faSpinner,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { usePersonalNarrationJob } from '../../state/personalNarrationContext';
import { useT } from '../../i18n/I18nContext';

// NOTE: we intentionally don't ``useNavigate()`` here. This
// component is mounted as a SIBLING of <RouterProvider> in
// main.jsx (the provider has to wrap the router so an active
// job survives every navigation), which means the React Router
// context isn't available -- useNavigate() throws "may be used
// only in the context of a <Router>" and crashes the whole app
// on mount. ``window.location.assign`` does the same thing
// without requiring router context.

function formatElapsed(sec) {
  if (sec == null) return '0:00';
  const safeSec = Math.max(0, Math.round(sec));
  const m = Math.floor(safeSec / 60);
  const s = String(safeSec % 60).padStart(2, '0');
  return `${m}:${s}`;
}

export default function PersonalNarrationBackground() {
  const { activeJob, dismiss } = usePersonalNarrationJob();
  const t = useT();
  // Track the previous status so we only fire toasts on the
  // edge -- pending → ready, pending → failed -- not on every
  // re-render while pending (the elapsedSec tick would otherwise
  // re-fire the toast).
  const prevStatusRef = useRef(activeJob.status);

  useEffect(() => {
    const prev = prevStatusRef.current;
    const cur = activeJob.status;
    prevStatusRef.current = cur;
    if (prev === cur) return;

    // Pending → ready: success toast with a click-to-return CTA.
    if (cur === 'ready' && prev !== 'idle' && prev !== 'ready') {
      toast.success(
        t('reader.personal.bg.toast.ready', { title: activeJob.chapterTitle || '' }),
        { autoClose: 8000 },
      );
      return;
    }
    // Pending → failed: error toast with the reason.
    if (cur === 'failed' && prev !== 'idle' && prev !== 'failed') {
      const reasonKey =
        activeJob.error === 'timeout'
          ? 'reader.personal.bg.toast.timeout'
          : activeJob.error === 'quota'
            ? 'reader.personal.bg.toast.quota'
            : 'reader.personal.bg.toast.failed';
      toast.error(t(reasonKey), { autoClose: 10000 });
    }
  }, [activeJob.status, activeJob.error, activeJob.chapterTitle, t]);

  // Floating badge — only visible while a job is actively
  // pending / submitting. Hidden on ready (the toast covers
  // that) and on failed (toast also covers that).
  const isBusy = activeJob.status === 'pending' || activeJob.status === 'submitting';
  if (!isBusy) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[60] left-4 sm:left-auto sm:max-w-sm"
    >
      {/* Card shape (rounded-2xl) instead of pill so multi-line
          copy wraps cleanly on mobile -- a rounded-full pill
          with two lines of text reads as a tall lozenge that
          truncates poorly on narrow screens. items-start so the
          icon + dismiss anchor at the top while the text column
          grows naturally below. */}
      <div className="flex items-start gap-3 rounded-2xl bg-surface border border-border shadow-lg px-3 py-2.5 backdrop-blur">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/15 text-primary shrink-0 mt-0.5">
          <FontAwesomeIcon icon={faMicrophoneLines} className="text-xs" />
        </span>
        <div className="min-w-0 flex-1">
          {/* No truncate -- title may wrap to two lines on narrow
              viewports. ``break-words`` covers the edge case of a
              very long localized title (e.g. FR "Génération de
              votre narration…"). */}
          <p className="text-[12px] font-semibold text-text leading-snug break-words">
            {t('reader.personal.bg.badge.title')}
          </p>
          <p className="mt-0.5 text-[11px] text-secondText leading-snug break-words">
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-[10px] animate-spin mr-1 text-primary"
              aria-hidden="true"
            />
            <span className="tabular-nums">
              {formatElapsed(activeJob.elapsedSec)}
            </span>
            {' · '}
            {t('reader.personal.bg.badge.hint')}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('reader.personal.bg.badge.cancel')}
          title={t('reader.personal.bg.badge.cancel')}
          className="shrink-0 w-7 h-7 rounded-full text-secondText hover:bg-surfaceAlt hover:text-text transition inline-flex items-center justify-center mt-0.5 -mr-1"
        >
          <FontAwesomeIcon icon={faXmark} className="text-[11px]" />
        </button>
      </div>
    </div>
  );
}
